import { useRef, useState } from 'react';
import { API_BASE } from '../config';
import './UserExcelImport.css';

export default function UserExcelImport({ onRegistered }) {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [page, setPage] = useState(0);
  const invalid = rows.filter(row => row.errors.length);
  const visible = onlyErrors ? invalid : rows;
  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const readResponse = async response => {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (data.rows) setRows(data.rows);
      throw new Error(data.error || (response.status === 401 || response.status === 403 ? '관리자 로그인을 확인해주세요.' : '요청을 처리하지 못했습니다. 다시 시도해주세요.'));
    }
    return data;
  };

  const download = async () => {
    setBusy('download'); setError('');
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/excel-template`, { headers: headers() });
      if (!response.ok) await readResponse(response);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url; link.download = '평가자_등록_양식.xlsx';
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  };

  const upload = async (selected, mode = 'preview') => {
    if (!selected || busy) return;
    setError(''); setNotice(''); setPage(0);
    if (mode === 'preview') { setRows([]); setFile(null); setOnlyErrors(false); setShowPasswords(false); }
    if (!/\.xlsx$/i.test(selected.name)) { setError('.xlsx 파일을 선택해주세요. 아래 양식을 내려받아 작성할 수 있습니다.'); return; }
    if (selected.size > 2 * 1024 * 1024) { setError('파일 크기는 2MB 이하여야 합니다.'); return; }
    setBusy(mode);
    try {
      const body = new FormData(); body.append('file', selected); body.append('mode', mode);
      const response = await fetch(`${API_BASE}/api/admin/users/upload-excel`, { method: 'POST', headers: headers(), body });
      const data = await readResponse(response);
      if (mode === 'preview') { setRows(data.rows); setFile(selected); }
      else {
        setRows([]); setFile(null);
        setNotice(`${data.inserted}명을 등록했습니다. 사용자 목록과 등록 현황에 반영됩니다. 사업·사이트 필터가 적용되어 있으면 전체로 변경해 확인하세요.`);
        await onRegistered();
      }
    } catch (e) {
      setError(e.message);
      // A lost response may follow a committed transaction: require a fresh duplicate check.
      if (mode === 'register') { setFile(null); setNotice('등록 여부 확인을 위해 같은 파일을 다시 업로드해주세요.'); }
    } finally { setBusy(''); }
  };

  return <section className="user-excel" aria-labelledby="excel-heading" aria-busy={Boolean(busy)}>
    <div className="user-excel-heading">
      <div><h4 id="excel-heading">엑셀로 한 번에 등록</h4><p>양식을 작성하고 명단을 확인한 뒤 등록하세요.</p></div>
      <button type="button" disabled={Boolean(busy)} onClick={download}>{busy === 'download' ? '양식 준비 중…' : '↓ 엑셀 양식 다운로드'}</button>
    </div>
    <ol className="user-excel-steps"><li>양식 작성</li><li>파일 업로드 · 명단 확인</li><li>일괄 등록</li></ol>
    <div className="user-excel-drop" onDragOver={event => event.preventDefault()} onDrop={event => {
      event.preventDefault();
      if (busy) return;
      if (event.dataTransfer.files.length !== 1) { setError('파일을 하나씩 업로드해주세요.'); return; }
      upload(event.dataTransfer.files[0]);
    }}>
      <strong>{file ? file.name : '작성한 엑셀 파일을 여기에 놓으세요'}</strong>
      <span>이름 · 성별 · 출생년도 · 비밀번호 · 취약계층(예/아니오) · 취약계층 유형(저시력/전맹) / .xlsx / 최대 1,000명, 2MB</span>
      <input ref={input} type="file" accept=".xlsx" hidden onChange={event => { upload(event.target.files[0]); event.target.value = ''; }} />
      <button type="button" disabled={Boolean(busy)} onClick={() => input.current.click()}>{busy === 'preview' ? '명단 확인 중…' : file ? '다른 파일 선택' : '파일 선택'}</button>
    </div>
    <p>취약계층 유형은 저시력 또는 전맹으로 작성하세요. 취약계층이 예이고 유형이 빈칸이면 유형미지정으로 등록됩니다. 기존 4·5열 양식도 사용할 수 있으며, 취약계층이 빈칸이면 일반으로 등록됩니다.</p>
    {error && <p className="user-excel-error" role="alert">{error}</p>}
    {notice && <p className="user-excel-notice" role="status">{notice}</p>}
    {rows.length > 0 && <>
      <div className="user-excel-summary" aria-live="polite"><strong>전체 {rows.length}명</strong><span>등록 가능 {rows.length - invalid.length}명</span><span>확인 필요 {invalid.length}명</span></div>
      <div className="user-excel-options">
        <label><input type="checkbox" checked={onlyErrors} onChange={event => { setOnlyErrors(event.target.checked); setPage(0); }} /> 오류만 보기</label>
        <label><input type="checkbox" checked={showPasswords} onChange={event => setShowPasswords(event.target.checked)} /> 비밀번호 표시</label>
      </div>
      <div className="user-excel-table"><table><thead><tr><th>엑셀 행</th><th>이름</th><th>성별</th><th>출생년도</th><th>비밀번호</th><th>취약계층</th><th>취약계층 유형</th><th>확인 결과</th></tr></thead><tbody>
        {visible.slice(page * 50, (page + 1) * 50).map(row => <tr key={row.row} className={row.errors.length ? 'has-error' : ''}><td>{row.row}</td><td>{row.name || '—'}</td><td>{row.sex || '—'}</td><td>{row.birth || '—'}</td><td>{showPasswords ? row.phone || '—' : '••••••'}</td><td>{row.is_vulnerable === null ? '확인 필요' : row.is_vulnerable ? '예' : '아니오'}</td><td>{row.is_vulnerable === null ? '확인 필요' : row.is_vulnerable ? ({ low_vision: '저시력', blind: '전맹' }[row.vulnerability_type] || '유형미지정') : '—'}</td><td>{row.errors.length ? row.errors.join(' ') : '등록 가능'}</td></tr>)}
        {!visible.length && <tr><td colSpan="8">확인이 필요한 오류가 없습니다.</td></tr>}
      </tbody></table></div>
      {visible.length > 50 && <div className="user-excel-pagination"><button disabled={page === 0} onClick={() => setPage(page - 1)}>이전</button><span>{page + 1} / {Math.ceil(visible.length / 50)}</span><button disabled={(page + 1) * 50 >= visible.length} onClick={() => setPage(page + 1)}>다음</button></div>}
      <div className="user-excel-footer"><p>{invalid.length ? '표시된 행을 엑셀에서 수정한 뒤 다시 업로드해주세요. 모든 오류가 해결되면 등록할 수 있습니다.' : '명단을 확인했습니다. 아래 버튼을 누르면 전체 명단이 등록됩니다.'}</p><button className="user-excel-primary" disabled={Boolean(busy) || invalid.length > 0 || !file} onClick={() => upload(file, 'register')}>{busy === 'register' ? '등록 중…' : `${rows.length}명 일괄 등록`}</button></div>
    </>}
  </section>;
}
